import { useQuery } from "@apollo/client/react"
import { Download, ExternalLink } from "lucide-react"
import { useEffect } from "react"
import { Link, useParams } from "react-router-dom"
import { gql } from "~/__generated__"
import { dashboardPath } from "~/common/paths"
import { useDocumentTitle } from "~/common/use-document-title"
import { Button } from "~/ui/button"
import { GraphqlError } from "~/ui/graphql-error"
import { ScreenLoading } from "~/ui/screen-loading"

const labelQuery = gql(/* GraphQL */ `
  query PuzzleLabel($wpId: String!) {
    puzzle(wpId: $wpId) {
      id
      wpId
      title
      qrCodeUrl
    }
  }
`)

export const PuzzleLabelScreen = () => {
  const { wpId } = useParams<{ wpId: string }>()
  const { data, loading, error, startPolling, stopPolling } = useQuery(labelQuery, {
    variables: { wpId: wpId ?? "" },
    skip: !wpId,
  })
  const puzzle = data?.puzzle
  const qrCodeUrl = puzzle?.qrCodeUrl

  useEffect(() => {
    if (puzzle && !qrCodeUrl) {
      startPolling(2000)
      return () => stopPolling()
    }
  }, [puzzle, qrCodeUrl, startPolling, stopPolling])

  useDocumentTitle(puzzle ? `Sticker · ${puzzle.wpId}` : null)

  if (loading && !data) return <ScreenLoading />
  if (error) return <GraphqlError error={error} />
  if (!puzzle) {
    return (
      <main className="mx-auto max-w-2xl px-4 py-12">
        <h1 className="text-2xl font-semibold">Puzzle not found</h1>
        <Link to="/" className="mt-4 inline-block text-blue-600 underline">
          Back to home
        </Link>
      </main>
    )
  }

  if (!puzzle.qrCodeUrl) {
    return (
      <main className="mx-auto max-w-2xl px-4 py-12 text-center">
        <h1 className="text-2xl font-semibold">Generating your sticker…</h1>
        <p className="mt-3 text-base text-gray-700">
          This usually takes a few seconds. The page will refresh automatically.
        </p>
      </main>
    )
  }

  const pdfUrl = `/puzzles/${puzzle.wpId}/label.pdf`
  const pdfDownloadUrl = `${pdfUrl}?download=1`

  return (
    <main className="mx-auto max-w-3xl px-4 py-6 sm:py-10">
      <Link to={dashboardPath({})} className="text-sm text-gray-500 hover:text-gray-700">
        ← Back to dashboard
      </Link>

      <header className="mt-4">
        <h1 className="text-3xl font-semibold sm:text-4xl">Your puzzle&apos;s sticker</h1>
        <p className="mt-2 max-w-xl text-base leading-relaxed text-gray-700">
          Print this and stick it on the box. When someone scans the QR code, it sends them to your
          puzzle&apos;s journey page so they see where it&apos;s been and log their stop.
        </p>
      </header>

      <section className="mt-8 overflow-hidden rounded-xl border border-stone-200 bg-stone-50 shadow-sm">
        <div className="aspect-[11/6] w-full">
          <iframe
            title={`Sticker preview for ${puzzle.wpId}`}
            src={`${pdfUrl}#zoom=page-fit&toolbar=0&navpanes=0`}
            className="h-full w-full"
          />
        </div>
      </section>

      <section className="mt-6 flex flex-wrap items-center gap-3">
        <Button asChild className="h-12 px-5 text-base">
          <a href={pdfDownloadUrl} download={`wandering-puzzles-${puzzle.wpId}.pdf`}>
            <Download size={18} className="mr-2" aria-hidden />
            Download PDF
          </a>
        </Button>
        <Button asChild variant="outline" className="h-12 px-5 text-base">
          <a href={pdfUrl} target="_blank" rel="noreferrer">
            <ExternalLink size={18} className="mr-2" aria-hidden />
            Open in new tab
          </a>
        </Button>
        <p className="text-sm text-gray-600">
          Print from the PDF. That way the page header doesn&apos;t bleed onto your sticker.
        </p>
      </section>

      <section className="mt-10 rounded-xl border border-stone-200 bg-white p-6 text-base leading-relaxed text-gray-800 sm:p-7">
        <h2 className="font-display text-xl font-semibold">How to attach</h2>
        <ol className="mt-3 list-decimal space-y-2 pl-5">
          <li>Print at 100% scale on letter paper, sticker stock, or label sheets.</li>
          <li>Trim along the dashed border.</li>
          <li>
            Stick it on the back or side of the puzzle box, somewhere visible but where the lid can
            still open.
          </li>
          <li>
            Test by scanning with your phone camera. It should jump to the puzzle&apos;s journey
            page.
          </li>
        </ol>
      </section>

      <footer className="mt-10 text-base text-gray-700">
        <Link to={`/p/${puzzle.wpId}`} className="underline hover:text-gray-900">
          View this puzzle&apos;s public page
        </Link>
      </footer>
    </main>
  )
}
